/**
 * The emoji picker.
 *
 * Search looks at keywords rather than the glyph itself, so "lol" finds the
 * crying-laughing face. Custom emoji staff have uploaded sit in their own
 * first category and insert as :name: rather than a character.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { Tip } from './HoverCard';
import { fileUrl } from '@/lib/api';
import { emojiKeywords, emojiSet, FREQUENT, type CustomEmoji } from '@/lib/emoji';
import { useSession } from '@/stores/session';
import { useNarrow } from './TabBar';

const RECENT_KEY = 'pogu.emojiRecent';

function recents(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as string[]; }
  catch { return []; }
}

function remember(ch: string) {
  const next = [ch, ...recents().filter((c) => c !== ch)].slice(0, 24);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* storage disabled */ }
}

export function EmojiPicker({ onPick, onClose , anchor }: {
  /** The element that opened it. The panel is placed against this. */
  anchor?: HTMLElement | null; onPick: (text: string) => void; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);

  const panel = useRef<HTMLDivElement>(null);
  const [spot, setSpot] = useState<React.CSSProperties | null>(null);
  const categories = useMemo(() => emojiSet(), []);
  // Subscribed, not read once: a moderator uploading one, or an administrator
  // opening a reserved one up, has to reach a picker that is already open.
  const custom = useSession((s) => s.emoji);
  const recent = useMemo(() => recents(), []);

  // Escape closes from anywhere, including while the search box has focus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const q = query.trim().toLowerCase();

  const found = useMemo(() => {
    if (!q) return null;
    const chars: string[] = [];
    for (const cat of categories) {
      for (const ch of cat.list) if (emojiKeywords(ch).includes(q)) chars.push(ch);
    }
    return {
      chars,
      custom: custom.filter((c) => c.name.toLowerCase().includes(q))
    };
  }, [q, categories, custom]);

  function pick(ch: string) {
    remember(ch);
    onPick(ch);
  }

  function pickCustom(e: CustomEmoji) {
    onPick(':' + e.name + ':');
  }

  // Tab 0 is Recent when there is a history, otherwise the first real category.
  const tabs: { label: string; icon: string; render: () => React.ReactNode }[] = [];

  if (custom.length) {
    tabs.push({
      label: 'Custom',
      icon: 'sparkle',
      render: () => (
        <Grid>
          {custom.map((e) => <CustomCell key={e.id} emoji={e} onPick={pickCustom} />)}
        </Grid>
      )
    });
  }

  if (recent.length) {
    tabs.push({
      label: 'Recent',
      icon: 'history',
      render: () => (
        <Grid>
          {recent.map((ch) => (
            <button key={ch} type="button" onClick={() => pick(ch)} className={CELL} style={CELL_TEXT}>{ch}</button>
          ))}
        </Grid>
      )
    });
  }

  tabs.push({
    label: 'Popular',
    icon: 'clock',
    render: () => (
      <Grid>
        {FREQUENT.map((ch) => (
          <button key={ch} type="button" onClick={() => pick(ch)} className={CELL} style={CELL_TEXT}>{ch}</button>
        ))}
      </Grid>
    )
  });

  for (const cat of categories) {
    tabs.push({
      label: cat.label,
      icon: cat.icon,
      render: () => (
        <Grid>
          {cat.list.map((ch) => (
            <button key={ch} type="button" onClick={() => pick(ch)} className={CELL} style={CELL_TEXT}>{ch}</button>
          ))}
        </Grid>
      )
    });
  }

  const active = tabs[Math.min(tab, tabs.length - 1)];

  /*
   * Placed against whatever opened it, measured after the panel exists.
   *
   * It used to be absolutely positioned inside its parent, which meant it
   * landed wherever the nearest positioned ancestor happened to be - the
   * composer had one, a message row did not, so opening reactions threw the
   * panel to the top of the window.
   */
  useLayoutEffect(() => {
    if (!anchor) { setSpot({ right: 20, bottom: 20 }); return; }
    const a = anchor.getBoundingClientRect();
    const box = panel.current?.getBoundingClientRect();
    const w = box?.width || 336;
    const h = box?.height || 320;

    // Above the anchor by preference, below it when there is no room above.
    let top = a.top - h - 8;
    if (top < 8) top = Math.min(a.bottom + 8, window.innerHeight - h - 8);

    // Right-aligned to the anchor, pulled back inside the window.
    let left = a.right - w;
    if (left < 8) left = 8;
    if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8;

    setSpot({ left, top });
  }, [anchor]);

  /*
   * On a phone this is a keyboard, not a popover.
   *
   * It takes the bottom of the screen where the keyboard was, at about the
   * same height, and there is a key to go back to typing. A floating panel
   * anchored to a button is a mouse idea; on a touch screen it lands on top
   * of the conversation with the real keyboard still up underneath it.
   */
  const sheet = useNarrow();

  return createPortal(
    <>
      <span className="fixed inset-0 z-[80]" onClick={onClose} />
      <div
        ref={panel}
        data-keeps-card
        style={sheet ? undefined : (spot ?? { left: -9999, top: -9999 })}
        className={cn(
          'fixed z-[90] flex flex-col overflow-hidden border border-line bg-surface-2 shadow-[var(--shadow-2)]',
          sheet
            ? 'inset-x-0 bottom-0 h-[46vh] min-h-[280px] rounded-t-[calc(16px*var(--radius-mul,1))] border-x-0 border-b-0 pb-[env(safe-area-inset-bottom)] animate-[sheet-up_180ms_var(--ease-out-soft)]'
            : 'h-[380px] w-[366px] max-w-[calc(100vw-16px)] rounded-[var(--radius)] animate-[rise_160ms_var(--ease-out-soft)]'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* The grab handle, so it reads as a sheet you can dismiss. */}
        {sheet ? (
          <span aria-hidden className="mx-auto mt-2 h-1 w-9 flex-none rounded-full bg-surface-4" />
        ) : null}
        <div className="flex-none border-b border-line-soft p-2">
          <input
            // Focusing here on a phone pops the keyboard back up over the
            // sheet that just replaced it.
            autoFocus={!sheet}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search emoji"
            spellCheck={false}
            className="h-8 w-full rounded-[var(--radius-s)] border border-line bg-surface-1 px-2.5 text-[13px] outline-none focus:border-accent"
          />
        </div>

        <div className="flex min-h-0 flex-1">
          <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto p-2">
          {q ? (
            found && (found.chars.length || found.custom.length) ? (
              <Grid>
                {found.custom.map((e) => <CustomCell key={e.id} emoji={e} onPick={pickCustom} />)}
                {found.chars.map((ch) => (
                  <button key={ch} type="button" onClick={() => pick(ch)} className={CELL} style={CELL_TEXT}>{ch}</button>
                ))}
              </Grid>
            ) : (
              <p className="px-3 py-10 text-center text-[12.5px] text-mute">No emoji match that.</p>
            )
          ) : (
            active?.render()
          )}
          </div>

          {/* Categories down the right, as icons. A row of words across the
              top took more height than the grid it labelled. */}
          {q ? null : (
            <div className="no-scrollbar flex w-[42px] flex-none flex-col gap-0.5 overflow-y-auto border-l border-line-soft p-1.5">
              {tabs.map((t, i) => (
                <Tip key={t.label} text={t.label}>
                  <button
                    type="button"
                    aria-label={t.label}
                    onClick={() => { setTab(i); bodyRef.current?.scrollTo({ top: 0 }); }}
                    className={cn(
                      'grid h-[30px] w-[30px] flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] transition-colors',
                      i === tab ? 'bg-accent-soft text-accent-hi' : 'text-mute hover:bg-surface-3 hover:text-dim'
                    )}
                  >
                    <Icon name={t.icon} size={16} />
                  </button>
                </Tip>
              ))}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

// Bigger. At 19px on a 32px cell they were a wall of specks you had to lean
// in to read, and a flag at that size is a smudge.
const CELL_TEXT = { fontSize: '25px', lineHeight: 1 } as const;

const CELL =
  'grid h-10 w-10 place-items-center rounded-lg leading-none transition-[background,transform] hover:scale-110 hover:bg-surface-4';

function Grid({ children }: { children: React.ReactNode }) {
  // Seven across at the larger size, so the panel keeps its width.
  return <div className="grid grid-cols-7 gap-1">{children}</div>;
}

/**
 * One custom emoji in the grid.
 *
 * A locked one is shown rather than hidden: an emoji nobody can see is
 * indistinguishable from one that was never uploaded, and "why does he have
 * that one" is the entire point of reserving them for a plan.
 */
function CustomCell({ emoji, onPick }: { emoji: CustomEmoji; onPick: (e: CustomEmoji) => void }) {
  const locked = emoji.usable === false;
  return (
    <button
      key={emoji.id}
      type="button"
      disabled={locked}
      title={locked ? ':' + emoji.name + ': — ' + ACCESS_NOTE[emoji.access ?? 'everyone'] : ':' + emoji.name + ':'}
      onClick={() => onPick(emoji)}
      className={cn(CELL, 'relative', locked && 'cursor-not-allowed')}
      style={CELL_TEXT}
    >
      <img
        src={fileUrl(emoji.url)}
        alt={':' + emoji.name + ':'}
        className={cn('h-[22px] w-[22px] object-contain', locked && 'opacity-25 grayscale')}
      />
      {locked ? (
        <span className="absolute right-0.5 bottom-0.5 text-[8px] leading-none text-ghost">
          <Icon name="lock" size={9} />
        </span>
      ) : null}
    </button>
  );
}

/** Why a locked one is locked, in the words the staff console uses. */
const ACCESS_NOTE: Record<string, string> = {
  everyone: 'available to everyone',
  premium: 'Premium and above',
  vip: 'VIP only',
  staff: 'staff only'
};
